import React, { useState, useEffect, useRef } from "react";
import "./Settings.css";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import {
  SearchIcon,
  PrevIcon,
  NextIcon,
} from "../../../../utils/api-manager/Forms/SvgIcons";
import LoadingOverlay from "../../../common/LoadingBar.js";
import loader from "../../../../assets/Image/Loader2.gif";
const PayrollTable = ({
  data,
  columns,
  showAction,
  showFooter,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  totals,
  loading = false,

  onSearch,
  onPageChange,
  currentPage: controlledPage,
  totalPages: controlledTotalPages,
  searchTerm: controlledSearchTerm,
  enableServerSideSearch = false,
}) => {
  const {
    totalPaid,
    totalReceivedCredit,
    totalUnpaid,
    totalOutstandingCredit,
    totalDue,
    totalCredit,
    totalExpense,
    totalPaidPurchase,
    totalReceivedCreditPurchase,
    totalUnpaidForPurchases,
    totalOutstandingPurchase,
    totalDueForPurchases,
    totalPurchaseCredit,
    totalPurchase,
  } = totals;

    const [internalPage, setInternalPage] = useState(1);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [localSearchInput, setLocalSearchInput] = useState(""); 
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const currentPage = enableServerSideSearch ? controlledPage : internalPage;
  const searchTerm = enableServerSideSearch ? controlledSearchTerm : internalSearchTerm;
  const totalPages = enableServerSideSearch ? controlledTotalPages : Math.ceil(data.length / rowsPerPage);
  const setCurrentPage = enableServerSideSearch ? onPageChange : setInternalPage;
  const setSearchTerm = enableServerSideSearch ? onSearch : setInternalSearchTerm;
  const searchTimeout = useRef(null);

  const {formatCurrency} = useFormattingUtils();

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (enableServerSideSearch) {
      setLocalSearchInput(controlledSearchTerm || "");
    }
  }, [controlledSearchTerm, enableServerSideSearch]);

  const getDisplayedData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm) return dataToFilter;
    return dataToFilter.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const sortDisplayedData = (dataToSort) => {
    let sortableItems = [...dataToSort];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  const displayedData = enableServerSideSearch ? data : getDisplayedData();
  const filteredData = enableServerSideSearch ? data : filterData(displayedData);
  const sortedDisplayedData = sortDisplayedData(filteredData);

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const renderPagination = () => {
    const pages = [];
    const maxPages = 5;
    let start = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let end = Math.min(totalPages, start + maxPages - 1);

    if (end - start < maxPages) {
      start = Math.max(1, end - maxPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }
    return pages;
  };

  const statusStyles = {
    draft: {
      backgroundColor: "#D1D1EF",
      color: "#fff",
      borderRadius: "5px",
      padding: "5px 10px",
      width: "130px", 
      height: "30px", 
      fontSize:"14px"
    },
    unpaid: {
      backgroundColor: "#ff2024",
      color: "#fff",
      borderRadius: "5px",
      padding: "5px 20px",
      width: "130px", 
      height: "30px", 
      fontSize:"14px",
      textAlign: 'center',
      alignItems: 'center'
    },
    paid: {
      backgroundColor: "#198f51",
      color: "#fff",
      borderRadius: "5px",
      padding: "5px 20px",
      textAlign: 'center',
      alignItems: 'center',
      width: "130px", 
      height: "30px", 
      fontSize:"14px"
    },
    partially_paid: {
      backgroundColor: "#0c8ce9",
      color: "#fff",
      borderRadius: "5px",
      padding: "5px 20px",
      textAlign: 'center',
      alignItems: 'center',
      width: "130px", 
      height: "30px", 
      fontSize:"14px"
    },
  };
  const paymentMethodStyles = {
    bank: { backgroundColor: "" },
    "Business Cash": { backgroundColor: "" },
    "Credit Card": { backgroundColor: "" },
    cash: { backgroundColor: "" },
    cheque: { backgroundColor: "" },
    pay_later: { backgroundColor: "" },
    pay_now: { backgroundColor: "" },
    credit_invoice: { backgroundColor: "" },
  };

  const getPaymentMethodStyle = (method) => ({
    ...(paymentMethodStyles[method] || { backgroundColor: "#6c757d" }),
    borderRadius: "30px",
    padding: "5px 10px",
    color: "#000",
  });

  const downloadCSV = () => {
    const headers = columns.map((col) => col.header).join(",") + "\n";
    const rows = data
      .map((row) =>
        columns.map((col) => String(row[col.field] || "")).join(",")
      )
      .join("\n");

    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <LoadingOverlay loading={loading} loader={loader} />
    );
  }
  return (
    <div className="data-table-container mt-3">
      <div className="d-flex  mb-3">
        <div className="expense-searchcontainerstart d-flex" >
        <div className="search-container"style={{ marginLeft:"10px"}}
          >
            <input
              type="text"
              value={enableServerSideSearch ? localSearchInput : searchTerm}
              onChange={(e) => {
                const value = e.target.value;

                if (enableServerSideSearch) {

                  setLocalSearchInput(value);

                  clearTimeout(searchTimeout.current);
                  searchTimeout.current = setTimeout(() => {
                    setSearchTerm(value);
                  }, 300);
                } else {

                  setSearchTerm(value);
                }
              }}
              placeholder="Search..."
              style={{ height: "40px", backgroundColor: "#fff", border: '1px solid #DBDBDB', boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)" , }}
            />
            <SearchIcon />
          </div>

          {}
</div>
        <div style={{ marginRight: "67%" }}>

{}
</div>
</div>

<table className="data-table custom-table " style={{fontFamily: "inherit"}}>
        <thead className="table-header" >
          <tr>
            {columns.map((col, index) => (
              <th key={index} scope="col" onClick={() => handleSort(col.field)}>
                {col.header}
                {sortConfig.key === col.field &&
                  (sortConfig.direction === "ascending" ? (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 4L4 0L8 4H0Z" fill="#000000" />
                    </svg>
                  ) : (
                    <svg
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginLeft: "5px" }}
                    >
                      <path d="M0 0L4 4L8 0H0Z" fill="#000000" />
                    </svg>
                  ))}
              </th>
            ))}
            {showAction && (
              <th scope="col" className="action-column">
                Action$
              </th>
            )}
          </tr>
        </thead>
        <tbody className="table-body">
  {sortedDisplayedData.length === 0 ? (
    <tr>
      <td
        colSpan={columns.length + (showAction ? 2 : 1)}
        style={{
          textAlign: "center",
          
          fontWeight: "600",
          fontSize: "24px",
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.1) -92.86%, #4A5546 71.43%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        No data found
      </td>
    </tr>
  ) : (
    sortedDisplayedData.map((item) => (
      <tr key={item.id || item.index}>
        {columns.map((col, colIndex) => (
          <td>
            {col.field === "pay_method" ? (
              <span style={getPaymentMethodStyle(item[col.field])}>
                {item[col.field]
                  ? item[col.field]
                      .replace(/_/g, " ")
                      .split(" ")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      )
                      .join(" ")
                  : "-"}
              </span>
            ) : col.field === "status" ? (
              <td
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "30px",
                  border: "none",
                }}
              >
                <div
                  style={{
                    ...statusStyles[item[col.field]],
                    borderBottom: "1px solid #EBEAF2",
                  }}
                >
                  {item[col.field]
                    ? item[col.field]
                        .replace(/_/g, " ")
                        .split(" ")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        )
                        .join(" ")
                    : "-"}
                </div>
              </td>
            ) : Array.isArray(item[col.field]) ? (
              item[col.field].join(", ")
            ) : (
              item[col.field] ?? "-"
            )}
          </td>
        ))}
        {showAction && (
                  <td className="action-column">
                    {canEdit && (
                    <button
                      className="action-button"
                      onClick={() => onEdit(item)}
                    >
                      {}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.8067 4.695C14.0667 4.435 14.0667 4.00167 13.8067 3.755L12.2467 2.195C12 1.935 11.5667 1.935 11.3067 2.195L10.08 3.415L12.58 5.915M2 11.5017V14.0017H4.5L11.8733 6.62167L9.37333 4.12167L2 11.5017Z"
                          fill="#000000"
                        />
                      </svg>
                    </button>
                    )}
                    {canDelete && (
                    <button
                      className="action-button"
                      onClick={() => onDelete(item.id)}
                    >
                      {}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.00016 12.6667C4.00016 13.4 4.60016 14 5.3335 14H10.6668C11.4002 14 12.0002 13.4 12.0002 12.6667V6C12.0002 5.26667 11.4002 4.66667 10.6668 4.66667H5.3335C4.60016 4.66667 4.00016 5.26667 4.00016 6V12.6667ZM12.0002 2.66667H10.3335L9.86016 2.19333C9.74016 2.07333 9.56683 2 9.3935 2H6.60683C6.4335 2 6.26016 2.07333 6.14016 2.19333L5.66683 2.66667H4.00016C3.6335 2.66667 3.3335 2.96667 3.3335 3.33333C3.3335 3.7 3.6335 4 4.00016 4H12.0002C12.3668 4 12.6668 3.7 12.6668 3.33333C12.6668 2.96667 12.3668 2.66667 12.0002 2.66667Z"
                          fill="#000000"
                        />
                      </svg>
                    </button>
                    )}
          </td>
        )}
      </tr>
    ))
  )}
   {totalPages > 1 && (
     <tr>
       <td colSpan={columns.length + (showAction ? 2 : 1)} style={{ textAlign: "center" }}>
                   <ul className="pagination" style={{border:"none" , marginBottom:'5px' , marginTop:"5px"}}>
            <li className={`prev-next ${currentPage === 1 ? "disabled" : ""}`}>
            
            </li>
            {renderPagination()}
            <li
              className={`prev-next ${currentPage === totalPages ? "disabled" : ""}`}
            >
             
            </li>
          </ul>
       </td>
     </tr>
   )}
</tbody>
{showFooter && (
  <>
    {data.some((item) => item.type === "expense") && (
      <tr>
        <td colSpan={columns.length + (showAction ? 2 : 1)}>
          <div style={{ position: "relative", padding: "10px", marginRight: "50px" }}>
            {[
              { label: "Total Paid Amount", value: totalPaid },
              { label: "Total Received Credit", value: totalReceivedCredit },
              { label: "Total Unpaid", value: totalUnpaid },
              { label: "Total Outstanding Credit", value: totalOutstandingCredit },
              { label: "Total Due", value: totalDue },
              { label: "Total Credit", value: totalCredit },
              { label: "Total Expense Amount", value: totalExpense },
            ].map((total, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  paddingBottom: "18px",
                  alignItems: "center",
                  fontSize: "16px",
                  color: "#000000",
                  
                  fontWeight: 600,
                }}
              >
                <span style={{ flex: "1 1 auto", textAlign: "right", paddingRight: "20px" }}>
                  {total.label}
                </span>
                <span style={{ flex: "0 0 100px", textAlign: "left" }}>
                    {formatCurrency(total.value.toFixed(2))}
                </span>
              </div>
            ))}
          </div>

        </td>
      </tr>
    )}
    {data.some((item) => item.type === "purchase") && (
      <tr>
        <td colSpan={columns.length + (showAction ? 2 : 1)}>
          <div style={{ position: "relative", padding: "10px", marginRight: "50px" }}>
            {[
              { label: "Total Paid Amount", value: totalPaidPurchase },
              { label: "Total Received Credit", value: totalReceivedCreditPurchase },
              { label: "Total Unpaid", value: totalUnpaidForPurchases },
              { label: "Total Outstanding Credit", value: totalOutstandingPurchase },
              { label: "Total Due", value: totalDueForPurchases },
              { label: "Total Credit", value: totalPurchaseCredit },
              { label: "Total Expense Amount", value: totalPurchase },
            ].map((total, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  paddingBottom: "18px",
                  alignItems: "center",
                  fontSize: "16px",
                  color: "#000000",
                  
                  fontWeight: 600,
                }}
              >
                <span style={{ flex: "1 1 auto", textAlign: "right", paddingRight: "20px" }}>
                  {total.label}
                </span>
                <span style={{ flex: "0 0 100px", textAlign: "left" }}>
                {formatCurrency(total.value.toFixed(2))}
                </span>
              </div>
            ))}
          </div>
        </td>
      </tr>
    )}
  </>
)}
</table>

    </div>
  );
};

export default PayrollTable;